import { useMemo } from "react";
import { Sparkles, Trash2, RotateCcw, Sun, Sunrise, Sunset, Moon, Cookie, Images } from "lucide-react";
import { photoUrl } from "../lib/api";

const confidenceTone = {
  Low:    "bg-ochre-light text-ochre",
  Medium: "bg-sage-light text-sage",
  High:   "bg-sage-light text-sage",
};
const macroTone = "bg-sand text-slate2";

const timeIcon = {
  breakfast: { Icon: Sunrise, label: "Breakfast" },
  lunch:     { Icon: Sun, label: "Lunch" },
  snack:     { Icon: Cookie, label: "Snack" },
  dinner:    { Icon: Sunset, label: "Dinner" },
  late:      { Icon: Moon, label: "Late night" },
};

const kindLabel = {
  meal: "Meal",
  drink: "Drink",
  package: "Packaged",
};

export default function MealCard({ meal, onDelete, onLogAgain }) {
  const src = useMemo(() => photoUrl(meal.id), [meal.id]);
  const conf = confidenceTone[meal.confidence] || "bg-sand text-slate2";
  const t = timeIcon[meal.meal_time] || timeIcon.snack;
  const photoCount = meal.photo_urls?.length || 1;

  return (
    <article
      className="card-soft overflow-hidden animate-fade-up"
      data-testid={`meal-card-${meal.id}`}
    >
      <div className="relative">
        <img src={src} alt={meal.meal_name} className="w-full h-48 object-cover" loading="lazy" />
        <span
          className={`absolute top-3 right-3 inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${conf}`}
          data-testid={`meal-confidence-${meal.id}`}
        >
          <Sparkles size={14} strokeWidth={2} /> {meal.confidence} confidence
        </span>
        <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold bg-white/85 text-charcoal backdrop-blur">
          <t.Icon size={13} strokeWidth={2} /> {t.label}
        </span>
        {photoCount > 1 && (
          <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold bg-charcoal/70 text-cream backdrop-blur">
            <Images size={13} strokeWidth={2} /> {photoCount}
          </span>
        )}
      </div>
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-sage">{kindLabel[meal.kind] || "Meal"}</p>
            <h3 className="font-heading text-xl font-bold text-charcoal leading-tight mt-1" data-testid={`meal-name-${meal.id}`}>
              {meal.meal_name}
            </h3>
            <p className="text-xs text-slate2 mt-1">
              {meal.date}{meal.note ? ` · ${meal.note}` : ""}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {onLogAgain && (
              <button
                onClick={() => onLogAgain(meal.id)}
                data-testid={`meal-log-again-${meal.id}`}
                className="text-slate2 hover:text-sage tap-shrink rounded-full p-2"
                aria-label="Log again today"
                title="Log again today"
              >
                <RotateCcw size={18} strokeWidth={1.75} />
              </button>
            )}
            {onDelete && (
              <button
                onClick={() => onDelete(meal.id)}
                data-testid={`meal-delete-${meal.id}`}
                className="text-slate2 hover:text-terracotta tap-shrink rounded-full p-2"
                aria-label="Delete"
              >
                <Trash2 size={18} strokeWidth={1.75} />
              </button>
            )}
          </div>
        </div>

        <div className="mt-4">
          <p className="text-xs uppercase tracking-[0.15em] font-bold text-sage">Estimated calories</p>
          <p className="font-heading text-2xl font-extrabold text-charcoal mt-1" data-testid={`meal-calories-${meal.id}`}>
            {meal.calories_min}–{meal.calories_max} kcal
          </p>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <span className={`rounded-lg px-2 py-1 text-xs font-medium ${macroTone}`}>Protein · {meal.protein_level}</span>
          <span className={`rounded-lg px-2 py-1 text-xs font-medium ${macroTone}`}>Carbs · {meal.carb_level}</span>
          <span className={`rounded-lg px-2 py-1 text-xs font-medium ${macroTone}`}>Fat · {meal.fat_level}</span>
        </div>

        {meal.reason && <p className="mt-4 text-sm text-slate2 italic">{meal.reason}</p>}
        {meal.tip && (
          <div className="mt-4 rounded-2xl bg-sand p-4">
            <p className="text-xs uppercase tracking-[0.15em] font-bold text-sage mb-1">Gentle tip</p>
            <p className="text-sm text-charcoal leading-relaxed" data-testid={`meal-tip-${meal.id}`}>{meal.tip}</p>
          </div>
        )}
      </div>
    </article>
  );
}
